// ui/src/components/NotificationNudge.tsx
import React, { useState } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { usePushNotifications } from '../hooks/usePushNotifications';

const DISMISS_KEY = 'sproutly.notificationNudge.dismissedAt';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const recentlyDismissed = (): boolean => {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    return Number.isFinite(ts) && Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const NotificationNudge: React.FC = () => {
  const { isSupported, isIosSafari, isStandalone, permission, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(recentlyDismissed());
  const [busy, setBusy] = useState(false);

  // Only ask when we've never been answered; never when granted or denied.
  if (!isSupported || permission !== 'default' || dismissed) return null;

  const iosNeedsInstall = isIosSafari && !isStandalone;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribe();
    } finally {
      setBusy(false);
      setDismissed(true); // either it's on now, or the user answered the prompt
    }
  };

  return (
    <div
      data-testid="notification-nudge"
      className="mb-4 rounded-xl border border-primary-200 bg-primary-50 p-4 flex items-start gap-3"
    >
      <BellIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">Get notified of new messages</p>
        {iosNeedsInstall ? (
          <p className="mt-0.5 text-xs text-gray-600">
            Add this app to your Home Screen first, then you can turn on notifications.
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-gray-600">
            Turn on notifications so you don't miss messages when the app is closed.
          </p>
        )}
        {!iosNeedsInstall && (
          <button
            type="button"
            data-testid="notification-nudge-enable"
            disabled={busy}
            onClick={enable}
            className="mt-3 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            Turn on notifications
          </button>
        )}
      </div>
      <button
        type="button"
        data-testid="notification-nudge-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1"
      >
        &times;
      </button>
    </div>
  );
};

export default NotificationNudge;
